"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AssistantHistoryPanel = void 0;
const react_1 = __importStar(require("react"));
const marked_react_1 = __importDefault(require("marked-react"));
const moment_1 = __importDefault(require("moment"));
const classnames_1 = __importDefault(require("classnames"));
const _ui_1 = require("@ui");
const util_1 = require("@app/util");
require("./assistant-history-panel.scss");
// How much of the opening message becomes the row's label. Long enough to tell two
// conversations apart, short enough to stay on one line at the sidebar's width.
const LABEL_MAX_CHARS = 42;
/**
 * The archive's own markdown renderer — deliberately NOT the chat's.
 *
 * Three of the chat's overrides have no meaning here, and one of them would crash:
 * - a ```suggestions block renders the live quick-reply pills, which read the thread runtime.
 *   There is no runtime in this panel, and an archived question has nothing to answer.
 * - a ```chart block redraws a chart of figures that were true when it was sent. Rendering
 *   nothing is honest; re-rendering it as current is not.
 * - a `[[download:…]]` link points at a sandbox file that the 24h cleanup cron has almost
 *   certainly deleted, so in an archive it is a dead link by construction. It stays as text.
 *
 * Employee mentions also stay as plain text: opening a card out of an archived sentence invites
 * reading old information as current.
 */
const historyRenderer = {
    code(code, lang) {
        // An empty fragment rather than null: marked-react's renderer type demands an element.
        if (lang === 'suggestions' || lang === 'chart') {
            return react_1.default.createElement(react_1.default.Fragment, { key: `ds-ma-hist-${this.elementId}` });
        }
        return (react_1.default.createElement("pre", { key: `ds-ma-hist-${this.elementId}` },
            react_1.default.createElement("code", { className: (0, util_1.isValue)(lang) ? `lang-${lang}` : undefined }, code)));
    },
    link(href, text) {
        // Only genuinely external links stay clickable. Everything the agent minted for a live
        // session — employee cards, sandbox downloads — is stale in an archive.
        if (href.startsWith('http')) {
            return (react_1.default.createElement("a", { key: `ds-ma-hist-${this.elementId}`, href: href, target: '_blank', rel: 'noreferrer' }, text));
        }
        return react_1.default.createElement(react_1.default.Fragment, { key: `ds-ma-hist-${this.elementId}` }, text);
    },
};
// The label for a conversation in the list. The opening message is usually a quick-action
// prompt, which is several lines of instructions to the agent — so only its FIRST line is worth
// showing, and a naive truncation would produce "Vytvoř směny zaměstnanci. Zeptej se mě na: -
// jaké směny…" every time.
const conversationLabel = (conversation) => {
    const opening = conversation.messages.find((message) => message.role === 'user');
    const firstLine = (opening?.content ?? '').split('\n')[0].trim();
    if (firstLine === '')
        return '';
    return firstLine.length > LABEL_MAX_CHARS
        ? `${firstLine.slice(0, LABEL_MAX_CHARS).trimEnd()}…`
        : firstLine;
};
const HistoryMessage = ({ message }) => (react_1.default.createElement("div", { className: (0, classnames_1.default)('ds-ma-history-message', {
        'is-user': message.role === 'user',
    }) }, message.role === 'user' ? (react_1.default.createElement("span", { className: 'ds-ma-history-message-text' }, message.content)) : (react_1.default.createElement("div", { className: 'ds-ma-history-message-text' },
    react_1.default.createElement(marked_react_1.default, { isInline: false, gfm: true, breaks: true, renderer: historyRenderer }, message.content)))));
/**
 * The manager's own past conversations on this workspace, read-only.
 *
 * Read-only is the whole design, not a first cut. Resuming an archived conversation would be a
 * trap: n8n forgets a session after 60 idle minutes, so the agent would have no memory of
 * anything older than an hour, and reviving it would have to mint a new session id — which
 * spends another conversation from the workspace's monthly allowance. So there is no resume
 * control, and no delete either, because no mutation exists to back one.
 *
 * Two views in one panel: the list, and one conversation. Nothing is fetched when a row is
 * opened — a page already carries whole conversations.
 */
const AssistantHistoryPanel = ({ isLoading, hasLoaded, conversations, panelRef, onClose, }) => {
    const [openSessionId, setOpenSessionId] = (0, react_1.useState)(null);
    const open = conversations.find((c) => c.sessionId === openSessionId);
    return (react_1.default.createElement("div", { className: 'ds-ma-history-panel', "data-testid": 'ai-history-panel', ref: panelRef, role: 'dialog', "aria-label": (0, util_1.t)('HISTORY') ?? undefined },
        react_1.default.createElement("div", { className: 'ds-ma-history-head' },
            (0, util_1.isValue)(open) ? (react_1.default.createElement("button", { type: 'button', className: 'ds-ma-history-back', onClick: () => setOpenSessionId(null), "aria-label": (0, util_1.t)('BACK') ?? undefined, title: (0, util_1.t)('BACK') ?? undefined },
                react_1.default.createElement(_ui_1.Icon, { ico: 'back', size: _ui_1.Icon.SIZES.SMALL, color: _ui_1.Icon.COLORS.INHERIT }))) : null,
            react_1.default.createElement(_ui_1.Text, { type: _ui_1.Text.TYPES.BODY_LARGE, weight: _ui_1.Text.WEIGHTS.MEDIUM }, (0, util_1.t)('HISTORY')),
            react_1.default.createElement("button", { type: 'button', className: 'ds-ma-history-close', onClick: onClose, "aria-label": (0, util_1.t)('CLOSE') ?? undefined, title: (0, util_1.t)('CLOSE') ?? undefined },
                react_1.default.createElement(_ui_1.Icon, { ico: 'close4', size: _ui_1.Icon.SIZES.SMALL, color: _ui_1.Icon.COLORS.INHERIT }))),
        react_1.default.createElement("div", { className: 'ds-ma-history-body' },
            isLoading && (react_1.default.createElement("div", { className: 'ds-ma-history-loading' },
                react_1.default.createElement(_ui_1.Loader, { size: _ui_1.Loader.SIZES.TINY }))),
            !isLoading && hasLoaded && conversations.length === 0 && (react_1.default.createElement("span", { className: 'ds-ma-history-empty' }, (0, util_1.t)('TABLE_NO_DATA'))),
            !isLoading &&
                !(0, util_1.isValue)(open) &&
                conversations.map((conversation) => {
                    const label = conversationLabel(conversation);
                    return (react_1.default.createElement("button", { key: conversation.sessionId, type: 'button', className: 'ds-ma-history-row', "data-testid": 'ai-history-row', onClick: () => setOpenSessionId(conversation.sessionId) },
                        react_1.default.createElement("span", { className: 'ds-ma-history-row-date' }, (0, moment_1.default)(conversation.created).format('L LT')),
                        label !== '' && (react_1.default.createElement("span", { className: 'ds-ma-history-row-label' }, label))));
                }),
            (0, util_1.isValue)(open) && (react_1.default.createElement("div", { className: 'ds-ma-history-thread' }, open.messages.map((message, index) => (
            // No stable id on a message, and content can legitimately repeat, so the index
            // is the honest key here — the list is immutable once fetched.
            react_1.default.createElement(HistoryMessage, { key: index, message: message }))))))));
};
exports.AssistantHistoryPanel = AssistantHistoryPanel;
exports.default = AssistantHistoryPanel;
